// ============================================================================
// Storage adapter basado en cookies para Supabase JS.
//
// Supabase JS v2 usa localStorage por defecto, que es por-origen (por
// subdominio). Para compartir sesión entre app.vimosdigital.com y
// panel.vimosdigital.com, necesitamos usar cookies con domain=.vimosdigital.com.
//
// Este adapter implementa la interfaz StorageAsync que Supabase JS espera,
// pero escribe/lee en document.cookie en vez de localStorage.
// ============================================================================

/**
 * Serializa un cookie con los atributos especificados.
 * @param {string} name
 * @param {string} value
 * @param {CookieOptions} options
 */
function serializeCookie(name, value, options = {}) {
  let cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

  if (options.maxAge !== undefined) {
    cookie += `; Max-Age=${options.maxAge}`;
  }
  if (options.domain) {
    cookie += `; Domain=${options.domain}`;
  }
  if (options.path) {
    cookie += `; Path=${options.path}`;
  }
  if (options.secure) {
    cookie += '; Secure';
  }
  if (options.sameSite) {
    cookie += `; SameSite=${options.sameSite}`;
  }

  return cookie;
}

/**
 * Indica si el host actual pertenece al dominio configurado para compartir
 * la sesión. `domain` es `.vimosdigital.com` (o `vimosdigital.com`) y solo
 * tiene sentido cuando el propio navegador está servido desde ese dominio.
 *
 * @param {string} hostname - `window.location.hostname`
 * @param {string | undefined} domain - atributo Domain configurado.
 * @returns {boolean}
 */
function hostPerteneceAlDominio(hostname, domain) {
  if (!domain) return false;

  const dominioBase = domain.replace(/^\./, '').toLowerCase();
  const host = (hostname || '').toLowerCase();

  return host === dominioBase || host.endsWith(`.${dominioBase}`);
}

/**
 * Resuelve los atributos de cookie EFECTIVOS para el entorno actual.
 *
 * ¿Por qué no se usan los opciones tal cual? Porque un navegador descarta
 * SILENCIOSAMENTE (document.cookie ni siquiera lanza error) una cookie:
 *   - cuyo `Domain` no coincida con el host actual — en desarrollo local
 *     (http://localhost:5175) el host es `localhost`, que NO es subdominio
 *     de `.vimosdigital.com`, así que la cookie jamás se almacena;
 *   - marcada `Secure` servida por HTTP puro (Safari lo rechaza incluso en
 *     localhost).
 *
 * Sin esa cookie, supabase-js no encuentra la sesión en su storage
 * (auth-js lee SIEMPRE del storage en cada `getSession()`, sin caché en
 * memoria), y cada petición REST/RPC sale SIN el header
 * `Authorization: Bearer ...` → PostgREST la atiende como `anon` →
 * 401/42501 en la primera RPC del panel, aunque el login "haya funcionado"
 * (la sesión existía solo en el estado en memoria de la UI).
 *
 * Resolución:
 *   - Producción (https + subdominio real): Domain + Secure intactos.
 *   - Desarrollo/otros hosts: cookie host-only sin Secure — persiste en el
 *     origen actual, que es todo lo que el desarrollo local necesita.
 *
 * @param {CookieOptions} options
 * @returns {CookieOptions}
 */
function opcionesEfectivas(options = {}) {
  const loc = typeof globalThis.location !== 'undefined' ? globalThis.location : null;
  const hostname = loc?.hostname ?? '';
  const protocoloHttps = loc?.protocol === 'https:';

  const usarDominioCompartido = hostPerteneceAlDominio(hostname, options.domain);

  return {
    ...options,
    domain: usarDominioCompartido ? options.domain : undefined,
    secure: Boolean(options.secure) && protocoloHttps,
  };
}

/**
 * Parsea document.cookie y retorna un objeto con los cookies.
 * @returns {Record<string, string>}
 */
function parseCookies() {
  const cookies = {};
  const pairs = document.cookie.split(';');

  for (const pair of pairs) {
    const index = pair.indexOf('=');
    if (index === -1) continue;

    const key = pair.slice(0, index).trim();
    const value = pair.slice(index + 1).trim();

    if (key) {
      cookies[decodeURIComponent(key)] = decodeURIComponent(value);
    }
  }

  return cookies;
}

/**
 * Crea un storage adapter basado en cookies para Supabase JS.
 *
 * @param {CookieOptions} cookieOptions
 * @returns {import('@supabase/supabase-js').StorageAsync}
 */
export function createCookieStorage(cookieOptions = {}) {
  return {
    async getItem(key) {
      const cookies = parseCookies();
      return cookies[key] ?? null;
    },

    async setItem(key, value) {
      document.cookie = serializeCookie(key, value, opcionesEfectivas(cookieOptions));
    },

    async removeItem(key) {
      // Mismos atributos efectivos que setItem: un cookie solo se borra si
      // la sentencia de borrado replica sus atributos (Domain/Path).
      document.cookie = serializeCookie(key, '', {
        ...opcionesEfectivas(cookieOptions),
        maxAge: 0,
      });
    },
  };
}

/**
 * @typedef {object} CookieOptions
 * @property {string} [domain]
 * @property {number} [maxAge]
 * @property {string} [path]
 * @property {boolean} [secure]
 * @property {'lax' | 'strict' | 'none'} [sameSite]
 */
